// @ts-nocheck
import React from 'react';
import styled from 'styled-components';
import intl from 'react-intl-universal';
import { MenuItem, Button } from '@blueprintjs/core';
import { MultiSelect } from '@blueprintjs/select';
import { FormattedMessage as T } from '@/components';
import classNames from 'classnames';
import { CLASSES } from '@/constants/classes';
import { firstLettersArgs } from '@/utils';

const ProjectSelectMulti = MultiSelect.ofType();

/**
 * Project multi select field.
 */
export function ProjectMultiSelect({
  projects = [],
  selectedProjectIds = [],
  defaultSelectText = intl.get('select_projects'),
  popoverFill = false,
  onChange,
  ...multiSelectProps
}) {
  // Get selected projects from IDs
  const selectedProjects = React.useMemo(
    () => projects.filter((p) => selectedProjectIds.includes(p.id)),
    [projects, selectedProjectIds]
  );

  /**
   * Renders the project item.
   */
  const projectItemRenderer = (project, { handleClick, modifiers }) => {
    return (
      <MenuItem
        icon={<AvatarSelect text={project} />}
        disabled={modifiers.disabled}
        key={project.id}
        onClick={handleClick}
        text={project.name}
        selected={selectedProjectIds.includes(project.id)}
      />
    );
  };

  /**
   * Predicate for filtering projects.
   */
  const projectItemPredicate = (query, project) => {
    const normalizedTitle = project.name.toLowerCase();
    const normalizedQuery = query.toLowerCase();
    return `${project.name}. ${normalizedTitle}`.indexOf(normalizedQuery) >= 0;
  };

  /**
   * Handles project selection.
   */
  const handleProjectSelect = React.useCallback(
    (project) => {
      const newSelectedProjects = selectedProjectIds.includes(project.id)
        ? selectedProjects.filter((p) => p.id !== project.id)
        : [...selectedProjects, project];
      
      onChange?.(newSelectedProjects);
    },
    [selectedProjects, selectedProjectIds, onChange]
  );

  /**
   * Renders the tag for each selected project.
   */
  const tagRenderer = (project) => {
    return (
      <span>
        <AvatarSelect text={project} /> {project.name}
      </span>
    );
  };

  /**
   * Handles removing a project.
   */
  const handleTagRemove = (project, index) => {
    const newSelectedProjects = selectedProjects.filter((p) => p.id !== project.id);
    onChange?.(newSelectedProjects);
  };

  /**
   * Handles clearing all selected projects.
   */
  const handleClear = React.useCallback(() => {
    onChange?.([]);
  }, [onChange]);

  return (
    <ProjectSelectMulti
      items={projects}
      itemRenderer={projectItemRenderer}
      itemPredicate={projectItemPredicate}
      onItemSelect={handleProjectSelect}
      tagRenderer={tagRenderer}
      selectedItems={selectedProjects}
      tagInputProps={{
        onRemove: handleTagRemove,
        placeholder: defaultSelectText,
        rightElement: selectedProjects.length > 0 && (
          <Button minimal={true} icon="cross" onClick={handleClear} />
        ),
      }}
      popoverProps={{ minimal: true, boundary: 'window' }}
      fill={true}
      resetOnSelect={true}
      className={classNames(CLASSES.FORM_GROUP_LIST_SELECT, {
        [CLASSES.SELECT_LIST_FILL_POPOVER]: popoverFill,
      })}
      {...multiSelectProps}
    />
  );
}

const AvatarSelect = ({ text }) => {
  return <AvatarContent>{firstLettersArgs(text?.name)}</AvatarContent>;
};

const AvatarContent = styled.div`
  display: inline-block;
  background: #adbcc9;
  text-align: center;
  font-weight: 400;
  color: #fff;
  height: 22px;
  width: 22px;
  line-height: 22px;
  font-size: 12px;
`;
